// KmlOverlay.js
//
// Draws KML reference geometry (plot boundary, buffer, AB line, cable baseline)
// into the planning frame, behind the editing layer. Purely decorative and
// non-interactive: this is survey truth the operator traces against, never
// something they drag. Operator-drawn navigation lines are ordinary editor
// lines and are NOT handled here.

import React, { useRef, useState, useLayoutEffect } from 'react';

// `width` is the target thickness in SCREEN PIXELS; `dash` entries are
// MULTIPLES of the resolved stroke width, so the pattern scales with the line.
const ROLE_STYLE = {
  boundary: { stroke: '#ff3b30', width: 3, dash: null },
  buffer:   { stroke: '#ff9500', width: 2, dash: [4, 3] },
  guide:    { stroke: '#34c759', width: 2, dash: [6, 4] },
  other:    { stroke: '#8e8e93', width: 2, dash: [2, 3] },
};

export default function KmlOverlay({ features, frame, currentScale = 1, position = null }) {
  const svgRef = useRef(null);
  // Frame units per on-screen pixel. Measured rather than derived, because it
  // depends on BOTH the frame's unit density and the live pan/zoom transform.
  //
  // This is what previously made the KML invisible: stroke widths were quoted in
  // frame units copied from the editor's own constants, which assume a raster of
  // roughly one unit per screen pixel. A synthesised frame packs ~24 units per
  // pixel, so a nominal 15-unit stroke rendered 0.6 px wide and vanished -- and
  // it got worse as the canvas grew. Measuring the ratio makes the stroke a true
  // screen-pixel width, independent of both canvas size and zoom.
  const [unitsPerPx, setUnitsPerPx] = useState(null);
  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el || !frame) return;
    const w = el.getBoundingClientRect().width;  // post-transform, so zoom is included
    if (w > 0) setUnitsPerPx(frame.width / w);
  }, [frame, currentScale, position, features]);
  if (!features || features.length === 0 || !frame || !frame.lonLatToPixel) return null;
  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${frame.width} ${frame.height}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none',
        // Explicitly above the imagery. Relying on DOM order alone is fragile:
        // any z-index introduced inside the tile layer silently buries this.
        zIndex: 1,
      }}
    >
      {features.map((f) => {
        const st = ROLE_STYLE[f.role] || ROLE_STYLE.other;
        // Base widths are quoted at the editor's own scale (its lines use ~10);
        // the floor keeps the geometry findable when zoomed right out.
        // Fall back to a conservative ratio for the very first paint, before the
        // measurement lands; it is corrected on the same frame.
        const upp = unitsPerPx || frame.width / 1200;
        const strokeW = st.width * upp;
        const pts = f.coords.map(([lon, lat]) => frame.lonLatToPixel(lon, lat));
        if (pts.length < 2) return null;
        const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]} ${p[1]}`).join(' ') + (f.closed ? ' Z' : '');
        return (
          <path
            key={f.id}
            d={d}
            fill="none"
            stroke={st.stroke}
            // strokeW is already a screen-pixel width converted into frame units,
            // so it stays constant on screen at any zoom or canvas size. Do NOT
            // add vectorEffect="non-scaling-stroke": that only cancels transforms
            // inside the SVG's own coordinate system, not the CSS transform on an
            // ancestor, which is why an earlier version fattened up when zoomed.
            strokeWidth={strokeW}
            strokeDasharray={st.dash ? st.dash.map((n) => n * strokeW).join(' ') : undefined}
          />
        );
      })}
    </svg>
  );
}
